import React from 'react';
import PropTypes from 'prop-types';
import _find from 'lodash/find';
import _maxBy from 'lodash/maxBy';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { SRL_COMPONENT_TYPES } from '../../../../constants';
import SplitContentContainer from '../SplitContentContainer/SplitContentContainer';
import {
  selectAnswerChangeData,
  selectPercentageData,
  selectStackedBardChartData,
  selectTextListData,
  selectChartDataByDescription,
} from '../../../../utils';
import SRLStudentResponses from '../SRLStudentResponses/SRLStudentResponses';
import OffOnTrack, { reduceOnOffTrack } from "./SRLOffONTrack/SRLOffOnTrack";
import SRLBarSections from '../SRLBarSections/SRLBarSections';

import s from './SRLGoalsStudentsContent.module.css';

const getGoalStudentsContentGraph = (height, chartData, barKey) => {
  let highDomainLimit = 40;
  const maxData = _maxBy(chartData, item => Number(item.student_count));
  if (maxData) {
    highDomainLimit = Number(maxData.student_count);
  }
  return (
    <ResponsiveContainer height={height}>
      <BarChart
        data={chartData}
        margin={{ top: 20, right: 0, left: -20, bottom: 0 }}
        barCategoryGap="27%"
      >
        <Bar
          dataKey={barKey}
          fill="var(--prs-c-info-800)"
          minPointSize={0}
          label={{
            value: barKey,
            position: 'top',
            fontFamily: 'var(--prs-ff-sans)',
            fontSize: '14',
            fill: 'var(prs-c-gray-700)' }}
        />
        <XAxis
          dataKey="answer"
          tickLine={false}
          tick={{
            fill: 'var(prs-c-gray-700)',
            fontStyle: 'italic',
            fontSize: '12',
            fontWeight: 400,
          }}
        />
        <YAxis
          dataKey="Students"
          domain={[0, highDomainLimit]}
          label={{ value: 'total students',
            angle: -90,
            position: 'center',
            fontSize: 14,
            fontFamily: 'var(--prs-ff-sans)',
            fontStyle: 'italic',
            fill: 'var(prs-c-gray-700)',
          }}
          tick={false}
          axisLine={false}
          tickLine={false}
        />
        <CartesianGrid vertical={false} horizontal={false} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const ColumnBarGraph = ({ title, data, barKey }) => (
  <div>
    <div className={s.columnGraphTitle}>{title}</div>
    <div className={s.graph}>
      {getGoalStudentsContentGraph(180, data, barKey)}
    </div>
  </div>
);

ColumnBarGraph.propTypes = {
  title: PropTypes.object,
  data: PropTypes.array,
  barKey: PropTypes.string,
};

const getGraphTitle = aliasTitle => (
    aliasTitle === 'hope for' ?
        (<span>Grade students <b>hope for</b></span>) :
        (<span>Grade students would be <b>OK with</b></span>)
);

const getFormattedData = (data = []) => {
  const aliasAnswers = {
    'I just want to pass': 'Pass',
  };

  const clonedData = data.map((item) => {
    const cloned = {
      ...item
    };

    if (aliasAnswers[item.answer]) { // override long text by an alias
      cloned.answer = aliasAnswers[item.answer];
    }

    if (item.percent !== undefined) {
      cloned.percent = Math.round(item.percent);
    }

    return cloned;
  });

  return clonedData;
};

const getFormattedBarSectionData = (data) => {
  const yesAnwser = _find(data, { answer: 'Yes' });
  const noAnswer = _find(data, { answer: 'No' });
  const response = [];

  if (noAnswer) {
    response.push({
      label: 'Off track',
      value: noAnswer.student_count,
      color: 'red',
    });
  }

  if (yesAnwser) {
    response.push({
      label: 'On track',
      value: yesAnwser.student_count,
      color: 'blue',
    });
  }

  return response;
};

const getSRLIntro = (insightsData, isMobileXS) => {
  const hopeForData = selectChartDataByDescription(insightsData, SRL_COMPONENT_TYPES.COLUMN_CHART, 'Grade Hope For');
  const okWithData = selectChartDataByDescription(insightsData, SRL_COMPONENT_TYPES.COLUMN_CHART, 'Grade OK with');

  let leftColumn = null;
  let rightColumn = null;

  if (hopeForData.length) {
    leftColumn = <ColumnBarGraph data={getFormattedData(hopeForData)} title={getGraphTitle('hope for')} barKey="student_count" />;
  }

  if (okWithData.length) {
    rightColumn = <ColumnBarGraph data={getFormattedData(okWithData)} title={getGraphTitle()} barKey="student_count" />;
  }

  return (
    <SplitContentContainer
      leftColumnContent={leftColumn}
      rightColumnContent={rightColumn}
      borderPosition="center"
      stackedVersion={isMobileXS}
    />
  );
};


const getSRLCheckpoint = (insightsData, isMobileXS, isSRLFirstCheckpoint) => {
  const percentageData = selectPercentageData(insightsData);
  const stackedBarData = selectStackedBardChartData(insightsData);
  const answerChangeData = selectAnswerChangeData(insightsData);
  const onOffData = reduceOnOffTrack(answerChangeData);
  const [percentageOffYes] = percentageData;
  const { prompt, studentResponses } = selectTextListData(insightsData);

  let leftColumn = null;
  let rightColumn = null;

  if (percentageOffYes) {
    const intPercentage = Math.round(percentageOffYes.percent);
    leftColumn = (
      <div>
        <div className={s.offTrackTile}>
          <div className={s.percentage}>
            {intPercentage}<span>%</span>
          </div>
          <div>are <b>off track</b></div>
        </div>

        <div className={s.offOnTrackContainer}>
          {!isSRLFirstCheckpoint && (
            <OffOnTrack
              offTrack={onOffData && onOffData.offTrack}
              onTrack={onOffData && onOffData.onTrack}
            />
          )}
        </div>

        <div className={s.BarSectionContainer}>
          <SRLBarSections items={getFormattedBarSectionData(stackedBarData)} />
        </div>
      </div>
    );
  }

  if (studentResponses.length) {
    rightColumn = (
      <SRLStudentResponses
        prompt={prompt}
        studentResponses={studentResponses}
        responseLimit={2}
        lineLimit={4}
      />
    );
  }

  return (
    <SplitContentContainer
      leftColumnContent={leftColumn}
      rightColumnContent={rightColumn}
      stackedVersion={isMobileXS}
    />
  );
};

const SRLGoalsStudentsContent = ({ data, isMobileXS }) => {
  const { insightsData, isSRLIntroSurvey, isSRLFirstCheckpoint } = data;

  return isSRLIntroSurvey ?
      getSRLIntro(insightsData, isMobileXS) :
      getSRLCheckpoint(insightsData, isMobileXS, isSRLFirstCheckpoint);
};

SRLGoalsStudentsContent.propTypes = {
  data: PropTypes.shape({
    insightsData: PropTypes.array.isRequired,
    isSRLIntroSurvey: PropTypes.bool,
    isSRLFirstCheckpoint: PropTypes.bool,
  }),
  isMobileXS: PropTypes.bool,
};

export default SRLGoalsStudentsContent;
